import { ApolloQueryResult, useMutation } from '@apollo/client';
import clsx from 'clsx';
import React, { useState } from 'react';

import {
  Box,
  Button,
  Fade,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@material-ui/core';
import { Close, Save } from '@material-ui/icons';

import { Toast } from '../../../../components/Toast';
import { CREATE_TAG } from '../../../../queries/tag';
import { ITag } from '../../../../typings/tag';
import { getFriendlyErrorMessage } from '../../../../utils/errorHelper';
import useStyles from '../../styles';

interface ICreateTagModal {
  isOpened: boolean
  handleClose: () => void
  refetchList: (
    variables?: {
      listTags: ITag[]
    } | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<ApolloQueryResult<any>>
}

const CreateTagModal: React.FC<ICreateTagModal> = ({
  isOpened,
  handleClose,
  refetchList,
}) => {
  const [tagName, setTagName] = useState<string | undefined>();
  const [errors, setErrors] = useState<{[key: string]: boolean}>({});
  const classes = useStyles();

  const [createTag] = useMutation(CREATE_TAG);

  const handleSave = async () => {
    try {
      if (hasValidationError()) return;

      await createTag({
        variables: {
          name: tagName,
        },
      });

      Toast('Tag created!');

      handleClose();
      refetchList();
    } catch (error) {
      Toast(getFriendlyErrorMessage(error), 'error');
    }
  };

  const hasValidationError = () => {
    const errors = {
      ...(tagName?.trim() === '' && { tagName: true }),
    };

    setErrors(errors);

    if (Object.values(errors).length > 0) {
      return true;
    }

    return false;
  };

  return (
    <Modal open={isOpened} onClose={handleClose} className={classes.modal}>
      <Fade in={isOpened}>
        <Box className={classes.modalPaper}>
          <Box className={classes.modalHeader}>
            <Box className={classes.modalHeaderSide} />
            <Box className={classes.modalHeaderCenter}>
              <Typography align="center" variant="h4">
                Create Tag
              </Typography>
            </Box>
            <Box
              className={clsx(
                classes.modalHeaderSide,
                classes.modalCloseContainer,
              )}
            >
              <Box className={classes.modalClose}>
                <IconButton onClick={handleClose}>
                  <Close />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Box className={classes.modalInputs}>
            <TextField
              label="Name"
              variant="outlined"
              size="small"
              fullWidth
              error={errors.tagName}
              onChange={(e) => setTagName(e.target.value)}
            />
          </Box>

          <Button
            variant="contained"
            color="primary"
            className={classes.saveButton}
            onClick={handleSave}
            startIcon={<Save />}
          >
            Save
          </Button>
        </Box>
      </Fade>
    </Modal>
  );
};

export default CreateTagModal;
